import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { useReviewApi } from '@/components/admin/sessions/review/review-context';
import { ReviewNoteEditor } from '@/components/admin/sessions/review/review-note-editor';
import { ReviewStamp } from '@/components/admin/sessions/review/review-stamp';
import { DISABLED_INERT } from '@/components/ui/button';
import {
  type NoyauChoice,
  noyauChoiceOf,
  noyauOperation,
  pickCertainty,
  pickNoyau,
  pickSecond,
  toggleUnreadable,
} from '@/lib/review-gestures';
import { cn } from '@/lib/utils';
import { NOYAUX, type NoyauCode } from '@matter_extractor/shared';
import { useState } from 'react';

/**
 * The Noyau tab (relecture.md §8): the six noyaux in two columns, « Sûr » or
 * « Avec doute », a second candidate when doubtful, a note, or the issue « Pas
 * de noyau lisible ».
 *
 * Each press is a gesture and is sent at once - there is no « Enregistrer »
 * (§4.4). The choices the server would refuse (`reviewNoyauProblem`) cannot be
 * made: the second candidate is greyed on the noyau itself and absent while
 * sure, and « Pas de noyau lisible » must be lifted before a noyau is chosen.
 *
 * The noyau of the review never replaces the retained one, and the list does
 * not show it (D1): this tab is the only place it is read.
 */

const CHOICE =
  'flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-left text-caption focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function NoyauChoiceButton({
  code,
  pressed,
  second,
  disabled,
  onPick,
}: {
  readonly code: NoyauCode;
  readonly pressed: boolean;
  readonly second: boolean;
  readonly disabled: boolean;
  readonly onPick: () => void;
}) {
  const noyau = NOYAUX.find((entry) => entry.code === code);
  return (
    <button
      type="button"
      data-testid={second ? REVIEW_TEST_IDS.noyauSecond : REVIEW_TEST_IDS.noyauChoice}
      data-noyau={code}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onPick}
      className={cn(
        CHOICE,
        pressed && 'border-2 border-bleu-nuit bg-bleu-nuit-tint',
        pressed && second && 'border-dashed',
        disabled && DISABLED_INERT,
        disabled && 'cursor-not-allowed',
      )}
    >
      <span
        aria-hidden="true"
        className="size-3 shrink-0 rounded-pill"
        style={{ backgroundColor: noyau?.color }}
      />
      <span>
        <b className="font-bold">{code}</b> {noyau?.name}
      </span>
    </button>
  );
}

export function ReviewNoyau() {
  const { review, apply, canWrite, busy } = useReviewApi();
  const [editingNote, setEditingNote] = useState(false);
  const choice = noyauChoiceOf(review.noyau);
  const off = !canWrite || busy;

  const send = (next: NoyauChoice): void => {
    const operation = noyauOperation(next);
    if (operation !== null) void apply([operation]);
  };

  const saveNote = async (text: string): Promise<void> => {
    const operation = noyauOperation({ ...choice, note: text.trim() });
    if (operation === null) return;
    const applied = await apply([operation]);
    if (applied) setEditingNote(false);
  };

  return (
    <div className="flex flex-col gap-2.5">
      <div className="grid grid-cols-2 gap-1.5">
        {NOYAUX.map((noyau) => (
          <NoyauChoiceButton
            key={noyau.code}
            code={noyau.code}
            pressed={choice.noyau === noyau.code}
            second={false}
            disabled={off || choice.unreadable}
            onPick={() => send(pickNoyau(choice, noyau.code))}
          />
        ))}
      </div>

      {choice.noyau === null || choice.unreadable ? null : (
        <>
          <fieldset className="m-0 inline-flex min-w-0 self-start overflow-hidden rounded-md border border-gray-200 p-0">
            <legend className="sr-only">Certitude</legend>
            {(['sure', 'doubtful'] as const).map((certainty) => (
              <button
                key={certainty}
                type="button"
                data-testid={REVIEW_TEST_IDS.certainty}
                data-certainty={certainty}
                aria-pressed={choice.certainty === certainty}
                disabled={off}
                onClick={() => send(pickCertainty(choice, certainty))}
                className={cn(
                  'cursor-pointer px-3 py-0.5 font-semibold text-caption text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  choice.certainty === certainty &&
                    (certainty === 'sure'
                      ? 'bg-bleu-nuit text-white'
                      : 'bg-jaune-tint text-jaune-deep'),
                  off && 'cursor-not-allowed',
                )}
              >
                {certainty === 'sure' ? 'Sûr' : 'Avec doute'}
              </button>
            ))}
          </fieldset>
          {choice.certainty === 'doubtful' ? (
            <>
              <span className="font-semibold text-gray-600 text-xs">
                Ou bien, peut-être (facultatif)
              </span>
              <div className="grid grid-cols-2 gap-1.5">
                {NOYAUX.map((noyau) => (
                  <NoyauChoiceButton
                    key={noyau.code}
                    code={noyau.code}
                    pressed={choice.second === noyau.code}
                    second
                    disabled={off || noyau.code === choice.noyau}
                    onPick={() => send(pickSecond(choice, noyau.code))}
                  />
                ))}
              </div>
            </>
          ) : null}
        </>
      )}

      <button
        type="button"
        data-testid={REVIEW_TEST_IDS.unreadable}
        aria-pressed={choice.unreadable}
        disabled={off}
        onClick={() => send(toggleUnreadable(choice))}
        className={cn(
          'inline-flex cursor-pointer items-center self-start rounded-md border-2 border-gray-200 bg-white px-2.5 py-1 font-semibold text-caption text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          choice.unreadable && 'border-bleu-nuit bg-bleu-nuit-tint text-bleu-nuit',
          off && `cursor-not-allowed ${DISABLED_INERT}`,
        )}
      >
        {choice.unreadable ? 'Pas de noyau lisible ✓' : 'Pas de noyau lisible'}
      </button>

      {editingNote ? (
        <ReviewNoteEditor
          label="Note du noyau"
          initial={choice.note}
          busy={busy}
          onSave={(text) => void saveNote(text)}
          onCancel={() => setEditingNote(false)}
        />
      ) : choice.note === '' ? null : (
        <span
          data-testid={REVIEW_TEST_IDS.noteText}
          className="rounded-sm bg-gray-50 px-2 py-0.5 text-caption text-gray-700"
        >
          {choice.note}
        </span>
      )}
      {canWrite && !editingNote && (choice.noyau !== null || choice.unreadable) ? (
        <button
          type="button"
          data-testid={REVIEW_TEST_IDS.noteOpen}
          className="cursor-pointer self-start font-semibold text-bleu-nuit text-xs hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => setEditingNote(true)}
        >
          {choice.note === '' ? '+ Note sur le noyau' : 'modifier la note'}
        </button>
      ) : null}

      {review.noyau === null ? (
        <p className="text-caption text-gray-500 italic">Aucun noyau posé.</p>
      ) : (
        <ReviewStamp stamp={review.noyau} />
      )}
    </div>
  );
}
